import { Box, Typography } from '@mui/material';
import { FiltersProps } from './matching-filters';
import { FiltersMenu, FiltersMenuButton, FiltersMenuContext, FiltersMenuProvider } from '../filters-menu';
import { Button, CheckboxGroup, RadioControl } from '@uplift/ui-v2';
import { FilterAccordion, FilterAccordionGroup } from '../filter-accordion';
import { useCommunityExpertiseGeneralQuery, useEthnicitiesQuery, useFaithsQuery, useGendersQuery, useInsuranceCompaniesQuery, useSpecialties } from '@uplift/queries';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { SELF_PAY_ID } from '../../onboarding/utils';
import { ProviderMedicalRole, SpecialtyConcernCategoryEnum } from '@uplift/types';
import { InputGrouped, InputGroupedSection, InputGroupedSectionTitle } from '../input-group';
import { AvailabilityDayOfWeekInterval, AvailabilityTimeOfDayInterval, InsuranceMetaGrouping } from '@uplift/api';
import { FilterList, PersonOutline, Schedule } from '@mui/icons-material';
import React, { useContext } from 'react';
import { useIsMobile } from '@uplift/hooks';
import { availabilityDayOfWeekOptions, availabilityTimeOfDayOptions } from '@uplift/utils';
import { TextOverflowFallback } from '../text-overflow-fallback';

enum MatchingFilterName {
  insurance = 'insurance',
  specialties = 'specialties',
  availabilityTimeOfDay = 'availabilityTimeOfDay',
  availabilityDayOfWeek = 'availabilityDayOfWeek',
  genders = 'genders',
  faiths = 'faiths',
  ethnicities = 'ethnicities',
  communities = 'communities',
}

type MatchingFiltersFormValues = {
  [MatchingFilterName.insurance]: string;
  [MatchingFilterName.specialties]: string[];
  [MatchingFilterName.availabilityTimeOfDay]: AvailabilityTimeOfDayInterval[];
  [MatchingFilterName.availabilityDayOfWeek]: AvailabilityDayOfWeekInterval[];
  [MatchingFilterName.genders]: string[];
  [MatchingFilterName.faiths]: string[];
  [MatchingFilterName.ethnicities]: string[];
  [MatchingFilterName.communities]: string[];
};

export const MatchingFiltersV2: React.VFC<FiltersProps> = props => {
  const isMobile = useIsMobile();
  const form = useFormik<MatchingFiltersFormValues>({
    initialValues: {
      [MatchingFilterName.insurance]: props.matchingData.insuranceId ?? SELF_PAY_ID,
      [MatchingFilterName.specialties]: props.matchingData.specialtyIds,
      [MatchingFilterName.availabilityTimeOfDay]: props.matchingData.availabilityTimeOfDay,
      [MatchingFilterName.availabilityDayOfWeek]: props.matchingData.availabilityDayOfWeek,
      [MatchingFilterName.genders]: props.matchingData.identityFilters.genderIds,
      [MatchingFilterName.faiths]: props.matchingData.identityFilters.faithIds,
      [MatchingFilterName.ethnicities]: props.matchingData.identityFilters.ethnicityIds,
      [MatchingFilterName.communities]: props.matchingData.experienceFilters.communityIds,
    },
    onSubmit: (values) => {
      props.onUpdateFilters({
        ...props.matchingData,
        insuranceId: values[MatchingFilterName.insurance] ?? SELF_PAY_ID,
        specialtyIds: values[MatchingFilterName.specialties],
        availabilityTimeOfDay: values[MatchingFilterName.availabilityTimeOfDay],
        availabilityDayOfWeek: values[MatchingFilterName.availabilityDayOfWeek],
        identityFilters: {
          genderIds: values[MatchingFilterName.genders],
          faithIds: values[MatchingFilterName.faiths],
          ethnicityIds: values[MatchingFilterName.ethnicities],
        },
        experienceFilters: {
          ...props.matchingData.experienceFilters,
          communityIds: values[MatchingFilterName.communities],
        }
      });
    },
    enableReinitialize: true,
  });
  const restrictFiltersForPsych = props.matchingData.medicalRole === ProviderMedicalRole.psychiatrist;
  const disabledInsurance = props.disableInsuranceSelect || ((props.isBookingBoth || props.isStudentSecondBooking) && restrictFiltersForPsych);

  if (restrictFiltersForPsych) {
    return (
      <FormikProvider value={form}>
        <Box display="flex" gap={1} className={props.className}>
          <div>
            <InsuranceFilter
              stateId={props.matchingData.stateId}
              insuranceId={props.matchingData.insuranceId ?? SELF_PAY_ID}
              disabled={disabledInsurance}
            />
          </div>
          <div>
            <SpecialtiesFilter
              specialtiesIds={props.matchingData.specialtyIds}
              onUpdate={(specialtiesIds: string[]) => props.onUpdateFilters({
                ...props.matchingData,
                specialtyIds: specialtiesIds,
              })}
            />
          </div>
          <div>
            <AvailabilityFilter
              availabilityTimeOfDay={props.matchingData.availabilityTimeOfDay}
              availabilityDayOfWeek={props.matchingData.availabilityDayOfWeek}
            />
          </div>
        </Box>
      </FormikProvider>
    );
  }

  if (isMobile) {
    return (
      <FormikProvider value={form}>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} className={props.className}>
          <Box gridColumn="1/3">
            <SpecialtiesAndInsuranceFilter
              stateId={props.matchingData.stateId}
              specialtiesIds={props.matchingData.specialtyIds}
              insuranceId={props.matchingData.insuranceId ?? SELF_PAY_ID}
              insuranceDisabled={disabledInsurance}
            />
          </Box>
          <AvailabilityFilter
            availabilityTimeOfDay={props.matchingData.availabilityTimeOfDay}
            availabilityDayOfWeek={props.matchingData.availabilityDayOfWeek}
          />
          <MoreFilter
            genderIds={props.matchingData.identityFilters.genderIds}
            faithIds={props.matchingData.identityFilters.faithIds}
            ethnicityIds={props.matchingData.identityFilters.ethnicityIds}
            communityIds={props.matchingData.experienceFilters.communityIds}
          />
        </Box>
      </FormikProvider>
    );
  }

  return (
    <FormikProvider value={form}>
      <Box display="flex" gap={1} className={props.className}>
        <div>
          <InsuranceFilter
            stateId={props.matchingData.stateId}
            insuranceId={props.matchingData.insuranceId ?? SELF_PAY_ID}
            disabled={disabledInsurance}
          />
        </div>
        <div>
          <SpecialtiesFilter
            specialtiesIds={props.matchingData.specialtyIds}
            onUpdate={(specialtiesIds: string[]) => props.onUpdateFilters({
              ...props.matchingData,
              specialtyIds: specialtiesIds,
            })}
          />
        </div>
        <div>
          <AvailabilityFilter
            availabilityTimeOfDay={props.matchingData.availabilityTimeOfDay}
            availabilityDayOfWeek={props.matchingData.availabilityDayOfWeek}
          />
        </div>
        <div>
          <MoreFilter
            genderIds={props.matchingData.identityFilters.genderIds}
            faithIds={props.matchingData.identityFilters.faithIds}
            ethnicityIds={props.matchingData.identityFilters.ethnicityIds}
            communityIds={props.matchingData.experienceFilters.communityIds}
          />
        </div>
      </Box>
    </FormikProvider>
  );
};

type MatchingFiltersMenuProviderProps = {
  children?: React.ReactNode;
};

const MatchingFiltersMenuProvider: React.FC<MatchingFiltersMenuProviderProps> = props => {
  const formik = useFormikContext();

  return (
    <FiltersMenuProvider onCloseCallback={(reason) => reason === 'cancel' && formik.resetForm()}>
      {props.children}
    </FiltersMenuProvider>
  );
};

const UpdateMatchesButton = () => {
  const { onClose } = useContext(FiltersMenuContext);
  const formik = useFormikContext();

  const onClick = () => {
    formik.handleSubmit();
    onClose('submit');
  };

  return (
    <Button
      disabled={!formik.dirty || !formik.isValid}
      color="secondary"
      variant="contained"
      fullWidth
      onClick={onClick}
    >
      Update Matches
    </Button>
  );
};

type InsuranceFilterProps = {
  stateId: string;
  insuranceId: string;
  disabled?: boolean;
};


const InsuranceFilter: React.FC<InsuranceFilterProps> = (props) => {
  const { data: companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });

  const label = companies.find(c => c.id === props.insuranceId)?.name || 'Insurance';
  const isSelfPay = props.insuranceId === SELF_PAY_ID;
  const displayLabel = isSelfPay ? 'Cash Pay' : label;

  return (
    <MatchingFiltersMenuProvider>
      <FiltersMenuButton isActive={!isSelfPay} disabled={props.disabled}>
        <Box display="flex" alignItems="center" gap={1} color="text.secondary" width="100%">
          <PersonOutline fontSize="small" />
          <Typography fontSize="small" color="text.primary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{displayLabel}</Typography>
        </Box>
      </FiltersMenuButton>
      <FiltersMenu
        title="Insurance"
        actions={<UpdateMatchesButton />}
      >
        <InsuranceFilterInput stateId={props.stateId} />
      </FiltersMenu>
    </MatchingFiltersMenuProvider>
  );
};

type SpecialtiesFilterProps = {
  specialtiesIds: string[];
  onUpdate: (specialtiesIds: string[]) => void;
};

const SpecialtiesFilter: React.FC<SpecialtiesFilterProps> = (props) => {
  const { data: specialties = [] } = useSpecialties();
  const label = specialties.filter(s => props.specialtiesIds.includes(s.id)).map(s => s.name).join(', ') || 'Concerns';

  return (
    <MatchingFiltersMenuProvider>
      <FiltersMenuButton isActive={!!props.specialtiesIds.length}>
        <Box display="flex" gap={1} color="text.secondary" width="100%">
          <PersonOutline fontSize="small" />
          <TextOverflowFallback height={20} fallback={<Typography color="text.primary" fontSize="small">{props.specialtiesIds.length} concerns selected</Typography>}>
            <Typography color="text.primary" fontSize="small">{label}</Typography>
          </TextOverflowFallback>
        </Box>
      </FiltersMenuButton>

      <FiltersMenu
        title="Concerns"
        actions={<UpdateMatchesButton />}
      >
        <SpecialtiesFilterInput />
      </FiltersMenu>
    </MatchingFiltersMenuProvider>
  );
};

const SpecialtiesFilterInput = () => {
  const { data: specialties = [] } = useSpecialties();

  return (
    <InputGrouped
      name={MatchingFilterName.specialties}
      options={specialties.sort((a, b) => a.name.localeCompare(b.name))}
      renderInput={inputProps => (
        <CheckboxGroup
          {...inputProps}
          options={inputProps.options.map(opt => ({ label: opt.name, value: opt.id }))}
        />
      )}
      getGroup={(option) => {
        switch (option.concernCategory) {
          case SpecialtyConcernCategoryEnum.general:
            return { order: 1, key: SpecialtyConcernCategoryEnum.general, title: 'General' };
          case SpecialtyConcernCategoryEnum.specific:
          default:
            return { order: 2, key: SpecialtyConcernCategoryEnum.specific, title: 'Specific' };
        }
      }}
    />
  );
};


type SpecialtiesAndInsuranceFilterProps = {
  stateId: string;
  specialtiesIds: string[];
  insuranceId: string;
  insuranceDisabled?: boolean;
};

const SpecialtiesAndInsuranceFilter: React.FC<SpecialtiesAndInsuranceFilterProps> = props => {
  const formik = useFormikContext<MatchingFiltersFormValues>();
  const { data: insuranceCompanies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });
  const { data: specialties = [] } = useSpecialties();
  const label = specialties.filter(s => props.specialtiesIds.includes(s.id)).map(s => s.name).join(', ') || 'Concerns';
  const insuranceLabel = insuranceCompanies.find(c => c.id === props.insuranceId)?.name || 'Cash Pay';

  return (
    <MatchingFiltersMenuProvider>
      <FiltersMenuButton isActive={!!props.specialtiesIds.length || props.insuranceId !== SELF_PAY_ID}>
        <Box display="flex" width="100%" gap={1} color="text.secondary">
          <PersonOutline fontSize="small" />
          <Box textAlign="left" flexGrow={1} overflow="hidden">
            <TextOverflowFallback fallback={<Typography color="text.primary" fontWeight={500}>{props.specialtiesIds.length} concerns selected</Typography>}>
              <Typography color="text.primary" fontWeight={500}>{label}</Typography>
            </TextOverflowFallback>
            <Typography variant="body2" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{insuranceLabel}</Typography>
          </Box>
        </Box>
      </FiltersMenuButton>

      <FiltersMenu
        title="Details about you"
        actions={<UpdateMatchesButton />}
      >
        <FilterAccordionGroup>
          <FilterAccordion
            id={MatchingFilterName.specialties}
            title="Concerns"
            valueLabels={specialties.filter(s => formik.values[MatchingFilterName.specialties].includes(s.id)).map(s => s.name)}
          >
            <SpecialtiesFilterInput />
          </FilterAccordion>
          <FilterAccordion
            id={MatchingFilterName.insurance}
            title="Insurance"
            hideClearButton
            hideCount
            valueLabels={[ insuranceLabel ]}
            disabled={props.insuranceDisabled}
          >
            <InsuranceFilterInput stateId={props.stateId} />
          </FilterAccordion>
        </FilterAccordionGroup>
      </FiltersMenu>

    </MatchingFiltersMenuProvider>
  );
};

type InsuranceFilterInputProps = {
  stateId: string;
};

export const InsuranceFilterInput: React.FC<InsuranceFilterInputProps> = (props) => {
  const { data: companies = [] } = useInsuranceCompaniesQuery({ stateId: props.stateId });

  const insuranceOptions = companies.map(c => ({ id: c.id, name: c.name, metaGrouping: c.metaGrouping }))
    .concat([ { id: SELF_PAY_ID, name: 'None—I’ll pay for sessions myself', metaGrouping: null } ]);

  return (
    <InputGrouped
      name={MatchingFilterName.insurance}
      options={insuranceOptions.sort((a, b) => a.name.localeCompare(b.name))}
      renderInput={inputProps => (
        <RadioControl
          {...inputProps}
          values={inputProps.options.map(opt => ({ label: opt.name, value: opt.id }))}
          color="secondary"
        />
      )}
      getGroup={(option) => {
        if (option.id === SELF_PAY_ID) {
          return { order: 0, key: SELF_PAY_ID, title: '' };
        }

        switch (option.metaGrouping) {
          case InsuranceMetaGrouping.medicaid:
            return { order: 3, key: 'medicaid', title: 'Medicaid' };
          case InsuranceMetaGrouping.medicare:
            return { order: 2, key: 'medicare', title: 'Medicare' };
          default:
            return { order: 1, key: 'partners', title: 'Uplift insurance partners' };
        }
      }}
    />
  );
};

type AvailabilityFilterProps = {
  availabilityTimeOfDay: string[];
  availabilityDayOfWeek: string[];
};

const AvailabilityFilter: React.FC<AvailabilityFilterProps> = props => {
  const count = props.availabilityTimeOfDay.length + props.availabilityDayOfWeek.length;

  return (
    <MatchingFiltersMenuProvider>
      <FiltersMenuButton isActive={!!count} chipCount={count}>
        <Box display="flex" alignItems="center" gap={1} color="text.secondary">
          <Schedule fontSize="small" color="inherit" />
          <Typography fontSize="small" color="text.primary">Availability</Typography>
        </Box>
      </FiltersMenuButton>
      <FiltersMenu
        title="Availability"
        actions={<UpdateMatchesButton />}
      >
        <AvailabilityFilterInput />
      </FiltersMenu>
    </MatchingFiltersMenuProvider>
  );
};

export const AvailabilityFilterInput = () => {
  return (
    <div>
      <InputGroupedSection>
        <InputGroupedSectionTitle>Time of Day</InputGroupedSectionTitle>
        <CheckboxGroup
          name={MatchingFilterName.availabilityTimeOfDay}
          options={availabilityTimeOfDayOptions}
        />
      </InputGroupedSection>
      <InputGroupedSection>
        <InputGroupedSectionTitle>Day of Week</InputGroupedSectionTitle>
        <CheckboxGroup
          name={MatchingFilterName.availabilityDayOfWeek}
          options={availabilityDayOfWeekOptions}
        />
      </InputGroupedSection>
    </div>
  );
};

type MoreFilterProps = {
  genderIds: string[];
  faithIds: string[];
  ethnicityIds: string[];
  communityIds: string[];
};

const MoreFilter: React.FC<MoreFilterProps> = props => {
  const formik = useFormikContext<MatchingFiltersFormValues>();
  const { data: genders = [] } = useGendersQuery();
  const { data: faiths = [] } = useFaithsQuery();
  const { data: ethnicities = [] } = useEthnicitiesQuery();
  const { data: communities = [] } = useCommunityExpertiseGeneralQuery();

  const selectedGenders = genders.filter(g => props.genderIds.includes(g.id));
  const selectedFaiths = faiths.filter(f => props.faithIds.includes(f.id));
  const selectedEthnicities = ethnicities.filter(e => props.ethnicityIds.includes(e.id));
  const selectedCommunities = communities.filter(c => props.communityIds.includes(c.id));
  const count = selectedGenders.length + selectedFaiths.length + selectedEthnicities.length + selectedCommunities.length;

  return (
    <MatchingFiltersMenuProvider>
      <FiltersMenuButton isActive={!!count} chipCount={count}>
        <Box display="flex" alignItems="center" gap={1} color="text.secondary">
          <FilterList fontSize="small" color="inherit" />
          <Typography fontSize="small" color="text.primary">More filters</Typography>
        </Box>
      </FiltersMenuButton>

      <FiltersMenu
        title="Details about you"
        actions={<UpdateMatchesButton />}
      >
        <FilterAccordionGroup>
          <FilterAccordion
            id={MatchingFilterName.genders}
            title="Gender"
            valueLabels={genders.filter(g => formik.values[MatchingFilterName.genders].includes(g.id)).map(g => g.name)}
            onClear={() => formik.setFieldValue(MatchingFilterName.genders, [])}
          >
            <CheckboxGroup
              name={MatchingFilterName.genders}
              options={genders.map(g => ({ label: g.name, value: g.id }))}
            />
          </FilterAccordion>

          <FilterAccordion
            id={MatchingFilterName.faiths}
            title="Faith"
            valueLabels={faiths.filter(f => formik.values[MatchingFilterName.faiths].includes(f.id)).map(f => f.name)}
            onClear={() => formik.setFieldValue(MatchingFilterName.faiths, [])}
          >
            <CheckboxGroup
              name={MatchingFilterName.faiths}
              options={faiths.map(f => ({ label: f.name, value: f.id }))}
            />
          </FilterAccordion>

          <FilterAccordion
            id={MatchingFilterName.ethnicities}
            title="Ethnicity"
            valueLabels={ethnicities.filter(e => formik.values[MatchingFilterName.ethnicities].includes(e.id)).map(e => e.name)}
            onClear={() => formik.setFieldValue(MatchingFilterName.ethnicities, [])}
          >
            <CheckboxGroup
              name={MatchingFilterName.ethnicities}
              options={ethnicities.map(e => ({ label: e.name, value: e.id }))}
            />
          </FilterAccordion>

          <FilterAccordion
            id={MatchingFilterName.communities}
            title="Community Expertise"
            valueLabels={communities.filter(c => formik.values[MatchingFilterName.communities].includes(c.id)).map(c => c.name)}
            onClear={() => formik.setFieldValue(MatchingFilterName.communities, [])}
          >
            <CheckboxGroup
              name={MatchingFilterName.communities}
              options={communities.map(c => ({ label: c.name, value: c.id }))}
            />
          </FilterAccordion>
        </FilterAccordionGroup>
      </FiltersMenu>

    </MatchingFiltersMenuProvider>
  );
};
